import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPricing } from "@/services/api";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, MessageCircle, Truck, Tag } from "lucide-react";

// The bulk offers shown on a book page.
//
// Built from the live rules rather than typed into the page, so changing a
// threshold or the per-copy price in Admin > Shipping & Discounts updates what
// buyers read here too. A page that still advertised 30 copies after the client
// moved it to 20 would be worse than no page at all.

const ENQUIRY_EMAIL = "info@aceasia.com.sg";

function moneyOff(rule, price) {
  if (!rule) return null;
  if (rule.type === "fixed_unit_price") {
    return {
      unit: rule.value,
      saving: Math.max(0, price - rule.value),
    };
  }
  if (rule.type === "percent") {
    const unit = price * (1 - rule.value / 100);
    return { unit, saving: price - unit };
  }
  return null;
}

/** Group every quantity rule that touches this product by its threshold. */
function buildTiers(settings, product, zonesById) {
  if (!settings || !product) return [];
  const applies = (r) =>
    r &&
    r.enabled !== false &&
    r.basis === "quantity" &&
    (r.scope === "order" || r.productId === product.id);

  const tiers = new Map();
  const slot = (threshold) => {
    if (!tiers.has(threshold)) tiers.set(threshold, { threshold, free: null, discount: null });
    return tiers.get(threshold);
  };

  for (const r of settings.freeDelivery || []) {
    if (applies(r)) slot(Number(r.threshold) || 0).free = r;
  }
  for (const r of settings.discounts || []) {
    if (applies(r)) slot(Number(r.threshold) || 0).discount = r;
  }

  return [...tiers.values()]
    .filter((t) => t.threshold > 1 && (t.free || t.discount))
    .sort((a, b) => a.threshold - b.threshold)
    .map((t) => ({
      ...t,
      zoneLabel: t.free?.zoneId ? zonesById[t.free.zoneId]?.label || "" : "",
      pricing: moneyOff(t.discount, Number(product.price) || 0),
    }));
}

export default function BulkOffers({ product }) {
  const [settings, setSettings] = useState(null);
  const { items, addItem, updateQty } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPricing().then(setSettings).catch(() => {});
  }, []);

  const zonesById = useMemo(() => {
    const map = {};
    for (const z of settings?.zones || []) map[z.id] = z;
    return map;
  }, [settings]);

  const tiers = useMemo(
    () => buildTiers(settings, product, zonesById),
    [settings, product, zonesById],
  );

  if (!product || tiers.length === 0) return null;

  // "Put that number of copies in the cart" — set the quantity rather than add
  // to it, so clicking twice doesn't quietly double the order.
  const order = (qty) => {
    const inCart = items.find((i) => i.id === product.id);
    if (inCart) updateQty(product.id, qty);
    else addItem(product, qty);
    navigate("/cart");
  };

  const enquire = (tier) => {
    const subject = `Bulk order enquiry — ${tier.threshold}+ copies of ${product.title}`;
    const message =
      `I would like to order ${tier.threshold} or more copies of ${product.title}.\n\n` +
      `Delivery address / country:\n\nPreferred delivery date:\n`;
    navigate(
      `/contact?subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}`,
    );
  };

  return (
    <section data-testid="bulk-offers" className="border border-[#946f36]/40 bg-[#FDFBF7]">
      <div className="px-6 md:px-8 py-5 border-b border-[#946f36]/30">
        <div className="overline text-[#946f36]">Bulk orders</div>
        <h2 className="font-serif-display text-2xl md:text-3xl mt-1.5 text-[#281a12]">
          Ordering for a school, library or event?
        </h2>
      </div>

      <div className="divide-y divide-[#946f36]/25">
        {tiers.map((tier) => (
          <div
            key={tier.threshold}
            data-testid={`bulk-tier-${tier.threshold}`}
            className="px-6 md:px-8 py-6 flex flex-col lg:flex-row lg:items-center gap-5"
          >
            <div className="flex-1">
              <div className="font-serif-display text-xl text-[#281a12]">
                For orders of {tier.threshold} copies and above
              </div>

              <ul className="mt-2.5 space-y-1.5 text-sm text-[#4A3728]">
                {tier.free && (
                  <li className="flex items-start gap-2">
                    <Truck className="w-4 h-4 text-[#946f36] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span>
                      <strong>Free delivery</strong>
                      {tier.zoneLabel ? (
                        <> to one shipping address in {tier.zoneLabel} only.</>
                      ) : (
                        <>.</>
                      )}
                    </span>
                  </li>
                )}
                {tier.pricing && (
                  <li className="flex items-start gap-2" data-testid={`bulk-tier-${tier.threshold}-price`}>
                    <Tag className="w-4 h-4 text-[#946f36] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span>
                      Discounted price of{" "}
                      <strong>SGD {tier.pricing.unit.toFixed(2)} per copy</strong>{" "}
                      (SGD {tier.pricing.saving.toFixed(2)} off the recommended retail
                      price of SGD {Number(product.price).toFixed(2)}).
                    </span>
                  </li>
                )}
              </ul>

              {tier.free && tier.zoneLabel && (
                <p className="mt-2.5 text-xs text-[#946f36] leading-relaxed">
                  Ordering from outside {tier.zoneLabel}? Write to our delivery team at{" "}
                  <a href={`mailto:${ENQUIRY_EMAIL}`} className="underline underline-offset-2">
                    {ENQUIRY_EMAIL}
                  </a>{" "}
                  with the details, or send us an enquiry below.
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-3 lg:shrink-0">
              <button
                onClick={() => order(tier.threshold)}
                data-testid={`bulk-order-${tier.threshold}`}
                className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-6 py-3.5 text-xs uppercase tracking-[0.18em] transition-colors inline-flex items-center gap-2"
              >
                <ShoppingBag className="w-3.5 h-3.5" strokeWidth={1.5} /> Order Now
              </button>
              {tier.free && (
                <button
                  onClick={() => enquire(tier)}
                  data-testid={`bulk-enquire-${tier.threshold}`}
                  className="border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-6 py-3.5 text-xs uppercase tracking-[0.18em] transition-colors inline-flex items-center gap-2"
                >
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Enquire Now
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
